// Copyright (c) 2026 DMG L&D, LLC. All rights reserved.
// Proprietary and confidential. See LICENSE for terms.

/**
 * Count words, characters, sentences, paragraphs, and lines in text.
 *
 * Input: UTF-8 bytes of a text string.
 * Output: structured count result.
 */

import type { Bytes } from '../types.ts';

export interface WordCountResult {
  words: number;
  characters: number;
  charactersNoSpaces: number;
  sentences: number;
  paragraphs: number;
  lines: number;
  readingTimeSeconds: number;
}

const WORDS_PER_MINUTE = 238; // average adult silent reading speed

export async function wordCount(input: Bytes): Promise<WordCountResult> {
  if (!(input instanceof Uint8Array)) {
    throw new Error('text.wordCount: input must be a Uint8Array');
  }

  const text = new TextDecoder().decode(input);

  if (text.trim().length === 0) {
    return {
      words: 0,
      characters: 0,
      charactersNoSpaces: 0,
      sentences: 0,
      paragraphs: 0,
      lines: 0,
      readingTimeSeconds: 0,
    };
  }

  const characters = [...text].length; // handle multi-byte correctly
  const charactersNoSpaces = [...text.replace(/\s/g, '')].length;

  // Word count: split on whitespace, filter empties
  const wordMatches = text.trim().split(/\s+/).filter(Boolean);
  const words = wordMatches.length;

  // Sentence count: split on sentence-ending punctuation followed by space or end
  const sentenceMatches = text.match(/[.!?]+(?:\s|$)/g);
  const sentences = sentenceMatches ? sentenceMatches.length : (words > 0 ? 1 : 0);

  // Paragraph count: blocks separated by 2+ newlines
  const paragraphs = text
    .split(/\n\s*\n/)
    .filter((p) => p.trim().length > 0).length || (words > 0 ? 1 : 0);

  // Line count
  const lines = text.split('\n').length;

  const readingTimeSeconds = Math.ceil((words / WORDS_PER_MINUTE) * 60);

  return {
    words,
    characters,
    charactersNoSpaces,
    sentences,
    paragraphs,
    lines,
    readingTimeSeconds,
  };
}
